<template>
  <aside class="aside-nave" :class="{ 'collapsed-sidebar': toggleSidebar }">
    <header class="aside-nave__brand">
      <img
        :src="
          toggleSidebar
            ? '/assets/img/logo-icon.svg'
            : '/assets/img/logo-brand.svg'
        "
        :class="toggleSidebar ? 'logo-icon' : 'logo-brand'"
        @click="toHome"
      />

      <i @click="toggleSidebarClick()" class="icon-nav-arrow_right"></i>
    </header>

    <article>
      <details-development :info="infoDetails" notice="warning" />
      <ul class="aside-nave__menu">
        <template v-for="(item, index) in sidebar" :key="index">
          <template v-if="item.title && item.active">
            <div class="aside-nave__menu-items">
              <li class="aside-nave__subtitle">
                {{ item.title }}
              </li>

              <template v-for="(menu, idx) in item.items" :key="idx">
                <template v-if="menu.subItems && menu.active">
                  <ul class="aside-nave__submenu">
                    <div
                      v-if="toggleSidebar"
                      class="aside-nave__submenu-list"
                      @click="toggleSubMenu(menu.subItems)"
                      :data-test-qa="menu.dataTestQa"
                      v-tooltip="{ value: menu.title, class: 'sidebar-tip' }"
                    >
                      <i class="dynamic-icon" :class="menu.icon" />
                    </div>

                    <div
                      v-else
                      class="aside-nave__submenu-list"
                      @click="toggleSubMenu(menu.subItems)"
                      :data-test-qa="menu.dataTestQa"
                    >
                      <i class="dynamic-icon" :class="menu.icon" />
                      {{ menu.title }}
                      <i
                        :class="
                          showSubmenuItem === JSON.stringify(menu.subItems)
                            ? 'icon-nav-bullet_up'
                            : 'icon-nav-bullet_down'
                        "
                      />
                    </div>

                    <template
                      v-for="(subitem, indexSubItem) in menu.subItems"
                      :key="indexSubItem"
                    >
                      <li
                        class="aside-nave__submenu-item"
                        v-if="
                          showSubmenuItem === JSON.stringify(menu.subItems) &&
                          subitem.active
                        "
                      >
                        <router-link
                          @click="activeMixPanel(subitem.name)"
                          :to="subitem.href"
                          :data-test-qa="subitem.dataTestQa"
                        >
                          <i class="icon-nav-bullet_right" />
                          <span>{{ subitem.name }}</span>
                        </router-link>
                      </li>
                    </template>
                  </ul>
                </template>

                <template v-else>
                  <li class="aside-nave__item" v-if="menu.active">
                    <router-link
                      :to="menu.href"
                      v-if="toggleSidebar"
                      @click="activeMixPanel(menu.name)"
                      v-tooltip="{ value: menu.name, class: 'sidebar-tip' }"
                    >
                      <i :class="menu.icon" />
                    </router-link>
                    <router-link
                      :to="menu.href"
                      @click="activeMixPanel(menu.name)"
                      v-else
                    >
                      <i :class="menu.icon" />
                      <span>{{ menu.name }}</span>
                    </router-link>
                  </li>
                </template>
              </template>
            </div>
          </template>
        </template>
      </ul>
    </article>
  </aside>
</template>

<script lang="ts">
import {
  onMounted,
  defineComponent,
  ref,
  computed,
  Ref,
  PropType,
  watch,
} from "vue";
import {
  ISidebarItems,
  IItem,
  ISubItem,
  infoDetails,
} from "@/common/hooks/useSidebarItems";
import { useRouter, useRoute } from "vue-router";
import AclModule from "@/common/module/acl";
import mixpanel from "mixpanel-browser";

export default defineComponent({
  name: "SideMenuNave",

  props: {
    sidebar: { type: Array as PropType<ISidebarItems[]>, required: true },
  },

  setup(prop) {
    const router = useRouter();
    const route = useRoute();
    const toggleSidebar = ref(false);
    const showSubmenuItem = ref("");

    const checkEnvironment = computed(() => {
      return AclModule.featureFlag("ENV_NOT_PROD");
    });

    const toHome = () => {
      router.push({ name: "insights" });
    };

    const toggleSidebarClick = () => {
      toggleSidebar.value = !toggleSidebar.value;

      if (toggleSidebar.value == true) showSubmenuItem.value = "";
    };

    const toggleSubMenu = (subItemSelected) => {
      if (toggleSidebar.value == true) toggleSidebar.value = false;
      const subItemString = JSON.stringify(subItemSelected);
      showSubmenuItem.value === subItemString
        ? (showSubmenuItem.value = "")
        : (showSubmenuItem.value = subItemString);
    };

    const searchItem = (
      item: ISidebarItems[] | IItem[] | ISubItem[],
      searchValue: string,
      persistValueFound: Ref<string> | null = null
    ) => {
      return item.some((el, i, array) => {
        if (el.items)
          return searchItem(el.items, searchValue, persistValueFound);
        else if (el.subItems)
          return searchItem(el.subItems, searchValue, persistValueFound);
        else if (persistValueFound && el.href === searchValue) {
          persistValueFound.value = JSON.stringify(array);
          return true;
        }
        return el.href === searchValue;
      });
    };

    const activeMixPanel = (name) => {
      const dictionary = {
        Offsite: "campanhas customizadas",
        Audiências: "audiências customizadas",
      };
      mixpanel.track("Acesso funcionalidade", {
        funcionalidade_acessada: dictionary[name] ? dictionary[name] : name,
        origem: "menu lateral",
      });
    };

    watch(
      () => route.path,
      (newValue) => {
        searchItem(prop.sidebar, newValue, showSubmenuItem);
      }
    );

    onMounted(() => {
      if (window.innerWidth < 1200) {
        toggleSidebar.value = true;
      }
    });

    return {
      toHome,
      toggleSidebar,
      toggleSubMenu,
      showSubmenuItem,
      toggleSidebarClick,
      checkEnvironment,
      infoDetails,
      activeMixPanel,
    };
  },
});
</script>

<style lang="scss">
.aside-nave {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  height: 100vh;
  width: 230px;
  transition: width 0.3s ease-out;
  background: var(--insights-color-18);

  > article {
    overflow-y: auto;
    overflow-x: hidden;
    height: calc(100% - 10rem);
    padding: 0 toRem(32px);
    margin-right: 5px;

    @media (max-width: 1915px) {
      padding: 0 idealWidth(35);
    }
  }

  &__brand-old {
    padding: 2.9rem toRem(32px) 3.25rem;
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    align-items: center;

    @media (max-width: 1915px) {
      padding: 2.9rem idealWidth(35) idealWidth(52) idealWidth(35);
    }

    > i {
      width: toRem(67px);
      height: toRem(23px);
      cursor: pointer;
      background-color: var(--insights-color-2);
    }

    i.icon-nav-arrow_right {
      color: var(--insights-color-2);
      background-color: transparent;
      display: inline-block;
      transform: rotate(180deg);
      vertical-align: middle;
      align-self: end;
      font-size: toRem(16px);
    }

    i.icon-nave-logo-brand {
      transition: all 0.4s ease;
    }

    i.icon-nave-logo-icon {
      transition: all 0.1s ease;
    }
  }

  &__brand {
    padding: 2.9rem toRem(32px) 3.25rem;
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    align-items: center;

    @media (max-width: 1915px) {
      padding: 2.9rem idealWidth(35) idealWidth(52) idealWidth(35);
    }

    > img {
      cursor: pointer;
    }

    > i {
      cursor: pointer;
      background-color: var(--insights-color-2);
    }

    > img.logo-brand {
      width: toRem(116px);
      height: toRem(52px);
      transition: all 0.4s ease;
    }

    > img.logo-icon {
      width: toRem(20px);
      height: toRem(30px);
      transition: all 0.1s ease;
    }

    i.icon-nav-arrow_right {
      color: var(--insights-color-2);
      background-color: transparent;
      display: inline-block;
      transform: rotate(180deg);
      vertical-align: middle;
      align-self: start;
      font-size: toRem(16px);
    }
  }

  &__submenu {
    padding: 0;
    margin-bottom: toRem(21px);
    font-size: toRem(16px);
    color: var(--insights-color-2);
    font-family: "text-medium";
  }

  &__submenu-list {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    cursor: pointer;

    &:hover {
      color: var(--insights-color-29);
    }

    .dynamic-icon {
      font-size: toRem(20px);
    }

    .icon-nav-bullet_up,
    .icon-nav-bullet_down {
      font-size: toRem(14px);
    }
  }

  &__submenu-item {
    list-style: none;
    margin-bottom: toRem(9px);

    &:last-child {
      margin-bottom: 0;
    }

    a {
      padding-left: toRem(28px);
      text-decoration: none;
      color: var(--insights-color-2);
      font-family: "text-bold";
      font-size: toRem(13px);
      display: flex;
      align-items: center;
      gap: toRem(5px);
      line-height: toRem(15px);

      &:first-child {
        margin-top: toRem(10px);
      }

      &:hover {
        color: var(--insights-color-29);
      }

      &.router-link-active {
        color: var(--insights-color-29);
      }

      i {
        flex-shrink: 0;
        font-size: toRem(10px);
      }
    }
  }

  &__menu {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__menu-items {
    margin-bottom: toRem(50px);
  }

  &__subtitle {
    text-transform: uppercase;
    margin-bottom: toRem(22px);
    color: #767676;
    font-family: "text-bold";
    font-size: toRem(13px);

    @media (max-width: 1915px) {
      margin-bottom: idealWidth(22);
    }
  }

  &__item {
    margin-bottom: toRem(21px);
    font-size: toRem(16px);

    @media (max-width: 1915px) {
      margin-bottom: idealWidth(21);
    }

    a {
      text-decoration: none;
      color: var(--insights-color-2);
      font-family: "text-medium";
      display: flex;
      align-items: center;
      gap: 0.6rem;

      &:hover {
        color: var(--insights-color-29);
      }

      &.router-link-active {
        color: var(--insights-color-29);
      }

      i {
        flex-shrink: 0;
        font-size: toRem(20px);
      }
    }
  }
  &.collapsed-sidebar {
    width: 85px;

    > article {
      padding: 0 28px;
      margin-right: 5px;
    }

    header {
      padding: 2.9rem 25px;
    }

    .aside-nave__brand {
      flex-direction: column;
      gap: 20px;
    }

    .aside-nave__brand-old {
      flex-direction: column;
      gap: 20px;
    }

    .aside-nave__subtitle {
      color: transparent;
      margin: 10px 0 5px;
    }

    .aside-nave__item a {
      justify-content: start;
      gap: 0;
      margin-bottom: 20px;
    }

    i.icon-nav-arrow_right {
      transform: rotate(0deg);
      align-self: auto;
      text-align: center;
    }
  }
}
.sidebar-tip {
  .p-tooltip-text {
    background-color: var(--insights-color-24) !important;
    font-size: 16px !important;
    font-weight: 500;
    padding: 6px 20px !important;
    color: var(--insights-color-2) !important;
  }
  .p-tooltip-arrow {
    border-right-color: var(--insights-color-24) !important;
  }
}
</style>
